import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Store, Copy, Sparkles } from "lucide-react";
import { getCurrentOwner, getOwnerShops } from "@/lib/auth";
import {
  createShopForOwner,
  getOwnerShops as fetchOwnerShops,
} from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Nueva función para clonar tienda (necesitamos agregarla al api.ts)
async function cloneShop(
  ownerId: string,
  data: {
    sourceShopId: string;
    name: string;
    address: string;
    owner: string;
    cloneOptions: {
      inventory: boolean;
      settings: boolean;
    };
  },
) {
  const response = await fetch(`/api/owners/${ownerId}/shops/clone`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Error clonando tienda");
  }

  return response.json();
}

export default function OwnerCreateShop() {
  const [, setLocation] = useLocation();
  const owner = getCurrentOwner();
  const returnUrl = new URLSearchParams(window.location.search).get("return") || "/owner/dashboard";

  // Estado para el modo (crear desde cero o clonar)
  const [creationMode, setCreationMode] = useState<"new" | "clone">("new");

  // Estados para clonación
  const [sourceShopId, setSourceShopId] = useState<string>("");
  const [cloneOptions, setCloneOptions] = useState({
    inventory: true,
    settings: true,
  });

  // Form data común
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    username: "",
    password: "",
    confirmPassword: "",
    managerName: "",
  });

  // Obtener tiendas del owner para clonar
  const { data: shops, isLoading: shopsLoading } = useQuery({
    queryKey: ["owner-shops-for-clone", owner?.id],
    queryFn: () => fetchOwnerShops(owner!.id),
    enabled: !!owner && creationMode === "clone",
  });

  // Cuando se selecciona una tienda para clonar, sugerir nombre automático
  useEffect(() => {
    if (sourceShopId && shops) {
      const selectedShop = shops.find((shop) => shop.id === sourceShopId);
      if (selectedShop) {
        // Sugerir nombre basado en la tienda clonada
        const suggestedName = `${selectedShop.name} - Nueva Sucursal`;
        if (!formData.name) {
          setFormData((prev) => ({ ...prev, name: suggestedName }));
        }

        // Sugerir usuario basado en el nombre
        const suggestedUsername = suggestedName
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "_")
          .replace(/_+/g, "_");

        if (!formData.username) {
          setFormData((prev) => ({ ...prev, username: suggestedUsername }));
        }
      }
    }
  }, [sourceShopId, shops]);

  // Mutaciones
  const createShopMutation = useMutation({
    mutationFn: (data: any) => createShopForOwner(owner!.id, data),
    onSuccess: () => {
      toast({
        title: "✅ Tienda creada!",
        description: "La nueva tienda ha sido creada exitosamente",
      });
      setLocation(returnUrl);
    },
    onError: (error: Error) => {
      toast({
        title: "❌ Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const cloneShopMutation = useMutation({
    mutationFn: (data: any) => cloneShop(owner!.id, data),
    onSuccess: (response) => {
      toast({
        title: "✅ Tienda clonada!",
        description: `Tienda creada con ${response.clonedData.inventory ? "inventario" : ""} ${response.clonedData.settings ? "y configuraciones" : ""} clonados`,
      });
      setLocation(returnUrl);
    },
    onError: (error: Error) => {
      toast({
        title: "❌ Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones comunes
    if (!formData.name || !formData.address || !formData.managerName) {
      toast({
        title: "Error",
        description: "Nombre, dirección y encargado son requeridos",
        variant: "destructive",
      });
      return;
    }

    if (creationMode === "new") {
      // Validación para creación nueva
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: "Error",
          description: "Las contraseñas no coinciden",
          variant: "destructive",
        });
        return;
      }

      if (!formData.username || !formData.password) {
        toast({
          title: "Error",
          description: "Usuario y contraseña son requeridos",
          variant: "destructive",
        });
        return;
      }

      createShopMutation.mutate({
        ownerId: owner!.id,
        name: formData.name,
        address: formData.address,
        owner: formData.managerName,
        username: formData.username,
        password: formData.password,
      });
    } else if (creationMode === "clone") {
      // Validación para clonación
      if (!sourceShopId) {
        toast({
          title: "Error",
          description: "Debes seleccionar una tienda para clonar",
          variant: "destructive",
        });
        return;
      }

      cloneShopMutation.mutate({
        sourceShopId,
        name: formData.name,
        address: formData.address,
        owner: formData.managerName,
        cloneOptions,
      });
    }
  };

  const isLoading = createShopMutation.isPending || cloneShopMutation.isPending;

  if (!owner) {
    return <Redirect to="/shop/login" />;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation(returnUrl)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Store className="h-6 w-6" />
                Crear Nueva Tienda
              </CardTitle>
              <CardDescription>
                Agrega un nuevo punto de venta a {owner.businessName}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tabs para seleccionar modo */}
          <Tabs
            value={creationMode}
            onValueChange={(value) => setCreationMode(value as "new" | "clone")}
            className="mb-6"
          >
            <TabsList className="grid grid-cols-2 mb-4">
              <TabsTrigger value="new" className="flex items-center gap-2">
                <Store className="h-4 w-4" />
                Crear desde cero
              </TabsTrigger>
              <TabsTrigger value="clone" className="flex items-center gap-2">
                <Copy className="h-4 w-4" />
                Clonar tienda existente
              </TabsTrigger>
            </TabsList>

            {/* Contenido para clonación */}
            <TabsContent value="clone" className="space-y-4">
              {shops && shops.length > 0 ? (
                <>
                  <div className="space-y-2">
                    <Label>Seleccionar tienda para clonar *</Label>
                    <Select
                      value={sourceShopId}
                      onValueChange={setSourceShopId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Elige una tienda..." />
                      </SelectTrigger>
                      <SelectContent>
                        {shops.map((shop) => (
                          <SelectItem key={shop.id} value={shop.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{shop.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {shop.address}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3 border rounded-lg p-4">
                    <Label className="text-sm font-medium">
                      ¿Qué deseas clonar?
                    </Label>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="clone-inventory"
                          checked={cloneOptions.inventory}
                          onCheckedChange={(checked) =>
                            setCloneOptions({
                              ...cloneOptions,
                              inventory: checked as boolean,
                            })
                          }
                        />
                        <Label
                          htmlFor="clone-inventory"
                          className="cursor-pointer"
                        >
                          <span className="font-medium">
                            Inventario completo
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Todos los productos con sus precios y umbrales de
                            stock bajo (stock comenzará en 0)
                          </p>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="clone-settings"
                          checked={cloneOptions.settings}
                          onCheckedChange={(checked) =>
                            setCloneOptions({
                              ...cloneOptions,
                              settings: checked as boolean,
                            })
                          }
                        />
                        <Label
                          htmlFor="clone-settings"
                          className="cursor-pointer"
                        >
                          <span className="font-medium">Configuraciones</span>
                          <p className="text-xs text-muted-foreground">
                            Configuración de Telegram, notificaciones, umbrales
                            de alerta
                          </p>
                        </Label>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-amber-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">
                          Ventajas de clonar:
                        </p>
                        <ul className="text-amber-700 list-disc list-inside mt-1 space-y-1">
                          <li>Ahorras tiempo configurando productos</li>
                          <li>
                            Mantienes consistencia de precios entre tiendas
                          </li>
                          <li>Configuraciones de alerta ya probadas</li>
                          <li>
                            Stock comienza en 0 - deberás realizar el primer
                            inventario
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <Store className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    No tienes tiendas para clonar. Crea tu primera tienda desde
                    cero.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setCreationMode("new")}
                  >
                    Crear desde cero
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Formulario común */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Tienda *</Label>
                <Input
                  id="name"
                  placeholder="Ej: Sucursal Centro"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="managerName">Nombre del Encargado *</Label>
                <Input
                  id="managerName"
                  placeholder="Ej: María López"
                  value={formData.managerName}
                  onChange={(e) =>
                    setFormData({ ...formData, managerName: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Dirección *</Label>
                <Input
                  id="address"
                  placeholder="Calle, número, ciudad"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  required
                />
              </div>

              {/* Solo mostrar usuario/contraseña si es creación nueva */}
              {creationMode === "new" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="username">Usuario *</Label>
                    <Input
                      id="username"
                      placeholder="sucursal_centro"
                      value={formData.username}
                      onChange={(e) =>
                        setFormData({ ...formData, username: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirmar Contraseña *
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </>
              )}
            </div>

            {/* Información del modo seleccionado */}
            <div className="text-sm text-muted-foreground">
              {creationMode === "new" ? (
                <p>
                  📝 <strong>Creación desde cero:</strong> La tienda comenzará
                  vacía, deberás configurar productos, precios y ajustes
                  manualmente.
                </p>
              ) : (
                <p>
                  🚀 <strong>Clonación:</strong>{" "}
                  {cloneOptions.inventory && "Productos y precios "}
                  {cloneOptions.inventory && cloneOptions.settings && "y "}
                  {cloneOptions.settings && "configuraciones "}
                  serán copiados automáticamente. Usuario y contraseña se
                  generarán automáticamente.
                </p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setLocation(returnUrl)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={
                  isLoading ||
                  (creationMode === "clone" && (!sourceShopId || shopsLoading))
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {creationMode === "new" ? "Creando..." : "Clonando..."}
                  </>
                ) : creationMode === "new" ? (
                  "Crear Tienda"
                ) : (
                  "Clonar Tienda"
                )}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              * La nueva tienda tendrá 30 días de prueba gratuita
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Necesitamos Redirect
import { Redirect } from "wouter";
